/**
 * Pure field-provenance logic for the /process ATLAS form: decides which tab
 * (Extracted vs AI Prefilled) a field belongs to from RECORDED state, instead
 * of re-deriving the ML mapping at render time. See spec
 * 2026-06-18-process-field-provenance-design.md.
 */

export type FieldTab = 'extracted' | 'ai_prefilled' | 'none';

export interface FieldTabInput {
  /** extractedData has a non-empty value at this path */
  hasValue: boolean;
  /** path was manually edited by the user (form's userEditedFields) */
  isUserEdited: boolean;
  /** path's value was applied from an ML prediction (recorded at apply-time) */
  isAiApplied: boolean;
  /** path has a prediction but no committed value yet (preview) */
  hasPendingPrediction: boolean;
  /** value's path exists (non-empty) in the raw document extraction snapshot.
   *  undefined ⇒ treat as document-sourced (back-compat for un-migrated callers). */
  isDocumentSourced?: boolean;
}

export function resolveFieldTab(input: FieldTabInput): FieldTab {
  if (input.isUserEdited) return 'extracted';                       // user owns the value
  if (input.isAiApplied) return 'ai_prefilled';                     // recorded AI provenance
  if (input.hasValue && (input.isDocumentSourced ?? true)) return 'extracted'; // from the document
  if (input.hasPendingPrediction) return 'ai_prefilled';            // preview of unapplied prediction
  return 'none';                                                    // non-document value OR empty ⇒ Other Fields
}

export interface PredictionEntryLike { value: string; confidence: number; }
export interface MLPredictionsLike {
  header?: Record<string, PredictionEntryLike>;
  position?: Record<string, PredictionEntryLike>;
  positions?: Array<Record<string, PredictionEntryLike>>;
}
export interface FieldLike { path: string; }
export interface MLMappingLike { atlasToML: Record<string, string> }

/**
 * Set of field paths that have ANY ml prediction, indexed correctly per
 * position line (positions[lineIndex], NOT position[0]). Used only for the
 * empty-field "pending prediction" preview case.
 */
export function buildPredictedFieldPaths(
  mlPredictions: MLPredictionsLike | null | undefined,
  fields: FieldLike[] | null | undefined,
  mapping: MLMappingLike | null | undefined,
): Set<string> {
  const out = new Set<string>();
  if (!mlPredictions || !Array.isArray(fields)) return out;
  const atlasToML = (mapping && mapping.atlasToML) || {};

  for (const field of fields) {
    const path = field && field.path;
    if (!path) continue;

    const posMatch = path.match(/^position\[(\d+)\]/);
    const isPosition = posMatch !== null;
    const fieldToCheck = isPosition ? path.replace(/^position\[\d+\]\./, 'position.') : path;

    const mlFieldName =
      atlasToML[fieldToCheck] ||
      atlasToML[fieldToCheck.replace('kopf.', '')] ||
      atlasToML[fieldToCheck.replace('position.', '')];
    if (!mlFieldName) continue;

    let hasPred = false;
    if (isPosition) {
      const lineIndex = parseInt(posMatch![1], 10);
      const line = Array.isArray(mlPredictions.positions) ? mlPredictions.positions[lineIndex] : undefined;
      hasPred = !!(line && line[mlFieldName]);
    } else {
      hasPred = !!(mlPredictions.header && mlPredictions.header[mlFieldName]);
    }
    if (hasPred) out.add(path);
  }
  return out;
}

/**
 * Set of field paths that hold a non-empty leaf in the raw document-extraction
 * snapshot, in the same path form fields use (`kopf.sendung.x`, `position[0].y`).
 * This is the authoritative "came from the document" set for tab classification.
 */
export function buildDocumentSourcedPaths(data: unknown): Set<string> {
  const out = new Set<string>();
  const walk = (node: unknown, path: string): void => {
    if (node === null || node === undefined) return;
    if (Array.isArray(node)) {
      node.forEach((v, i) => walk(v, `${path}[${i}]`));
    } else if (typeof node === 'object') {
      for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
        walk(v, path ? `${path}.${k}` : k);
      }
    } else {
      const s = String(node);
      if (s !== '' && s !== '[NULL]') out.add(path);
    }
  };
  walk(data, '');
  return out;
}
